import { Injectable } from '@angular/core';
import { Facebook } from '@awesome-cordova-plugins/facebook/ngx';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { Intercom } from '@awesome-cordova-plugins/intercom/ngx';
import { Smartlook, SmartlookCustomEvent } from '@ionic-native/smartlook/ngx';
import { Platform } from '@ionic/angular';
import 'firebase/analytics';
import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';
import 'firebase/storage';
import * as moment from 'moment-timezone';
import { BehaviorSubject } from 'rxjs';
import 'rxjs/add/operator/catch';

import { IGeneralConstants } from '@interfaces/general-constants';
import { IPortfolioConstants } from '@interfaces/portfolio-constants';
import { IPortfolioAssetsLabels } from '@interfaces/portfolios';
import Utils from '@util/util';
import { environment } from 'src/environments/environment';

const defaultConfig = {
  assetDetailsDefaultPeriod: '1D',
  defaultReferralCode: 'LUCA',
  depositDetailBannerButton: 'Invitar amigos',
  depositDetailBannerText: 'Gana hasta $100 mil para tu próxima Stock',
  giftDetailBannerButton: 'Compartir código',
  giftDetailBannerText: '¡Sigue ganando hasta $100 mil',
  invitePageInfoPortfolios: 'Ganarás el 0,84% de lo que inviertan en Portafolios por un año. ' +
                            'Ellos recibirán 1.000 pesos para que prueben Racional.',
  invitePageInfoStocks: 'Gana $10.000 por cada amigo/ que refieras a Pro, y ellos ' +
                        'recibirán $1.000 y 2 meses extras de Pro.',
  invitePageTitle: '¡Invita a tus amigos/as y gana!',
  referFriendsButton: 'Seguir ganando',
  referFriendsButtonDeposit: 'Obtén Stocks gratis',
  referralHomePillText: 'Stocks Gratis',
  showDepositDetailsReferFriendsBanner: 'false',
  showGiftDetailsButtons: 'true',
  showGiftDetailsReferFriendsBanner: 'false',
  stockDetailBannerText: 'Regala $1.000',
  stockDetailBannerTextStock: 'para invertir en Stocks como ',
  stockDetailShareCodeButtonText: 'Pasa el dato y gana',
  useDefaultCodeBannerAction: 'Quiero mis $1.000',
  useDefaultCodeBannerText: 'Gana $1.000 en Stocks como $assetName',
  defaultReturnStocksNotOwnProfile: 'totalReturn',
  defaultReturnPortfoliosNotOwnProfile: 'allTimeProfitPercentage',
  useDepositFirstBuy: 'false',
  showHomeOtherStocks: 'false',
  useDollarsInTransitBanner: 'Conviértete en Pro e inviértelos ahora',
  buyingPowerFirstBuy: 'true',
  searchDropdownDefaultOption: 'populars',
};

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {

  pdfOpen: boolean;
  firestore: firebase.firestore.Firestore;
  storage: firebase.storage.Storage;
  functions: firebase.functions.Functions;

  private generalConstantsSource = new BehaviorSubject<IGeneralConstants>(null);
  currentGeneralConstants = this.generalConstantsSource.asObservable();
  private portfolioConstantsSource = new BehaviorSubject<IPortfolioConstants>(null);
  currentPortfolioConstants = this.portfolioConstantsSource.asObservable();
  private holidaysSource = new BehaviorSubject<{ values: string[] }>(null);
  currentHolidays = this.holidaysSource.asObservable();
  private holidaysUSSource = new BehaviorSubject<{ values: string[] }>(null);
  currentUSHolidays = this.holidaysUSSource.asObservable();
  private portfolioLabelsSource = new BehaviorSubject<IPortfolioAssetsLabels>(null);
  currentPortfolioLabels = this.portfolioLabelsSource.asObservable();

  constructor(
    private firebaseNative: FirebaseX,
    private platform: Platform,
    private intercom: Intercom,
    private fb: Facebook,
    private smartlook: Smartlook,
  ) {
    this.initializeFirebase();
  }

  initializeFirebase() {
    if (!firebase.apps.length) {
      firebase.initializeApp(environment.FIREBASE_CONFIG);
    }

    if (!this.platform.is('cordova')) {
      firebase.analytics();
    }

    this.firestore = firebase.firestore();
    this.firestore.settings({
      ignoreUndefinedProperties: true,
      // host: 'localhost:8080',
      // ssl: false,
    } as firebase.firestore.Settings);
    this.storage = firebase.storage();
    this.functions = firebase.functions();
  }

  /// Firebase Server Timestamp
  get timestamp() {
    return firebase.firestore.FieldValue.serverTimestamp();
  }

  getCollection(collection: string): firebase.firestore.CollectionReference {
    return this.firestore.collection(collection);
  }

  getDocument(doc: string, collection: firebase.firestore.CollectionReference) {
    return collection.doc(doc).get().then((res) => {
      return res.data();
    })
    .catch((err) => {
      this.logError(err.message);
      console.error(`Error retrieving the document ${collection.id}/${doc} from firebase.`);
      throw (err);
    });
  }

  getAllDocuments(collection: firebase.firestore.CollectionReference) {
    return collection.get().then((res) => res)
    .catch((err) => {
      this.logError(err.message + collection.path);
      console.error('Error retrieving the documents from firebase.');
      throw (err);
    });
  }

  getDocumentObject(doc: string, collection: firebase.firestore.CollectionReference) {
    return collection.doc(doc).get().then((res) => {
      return res;
    })
    .catch((err) => {
      this.logError(err.message);
      console.log('Error retrieving the document ', doc, ' from firebase.');
      throw (err);
    });
  }

  addDocument(value: any, collection: firebase.firestore.CollectionReference) {
    const timestamp = this.timestamp;
    return collection.add({
      ...value,
      updatedAt: timestamp,
      createdAt: timestamp,
    });
  }

  addDocumentWithID(value: any, collection: firebase.firestore.CollectionReference, id: string) {
    const timestamp = this.timestamp;
    return collection.doc(id).set({
      ...value,
      updatedAt: timestamp,
      createdAt: timestamp,
    });

  }

  updateDocumentWithMerge(value: any, collection: firebase.firestore.CollectionReference, id: string) {
    const timestamp = this.timestamp;
    return collection.doc(id).set({
      ...value,
      updatedAt: timestamp,
    }, {
      merge:
      true,
    });
  }

  updateDocument(value: any, collection: firebase.firestore.CollectionReference, id: string) {
    const timestamp = this.timestamp;
    return collection.doc(id).update({
      ...value,
      updatedAt: timestamp,
    });
  }

  incrementValue(amount: number) {
    return firebase.firestore.FieldValue.increment(amount);
  }

  getDocumentsWithSingleQuery(
    collection: firebase.firestore.CollectionReference,
    param: string,
    queryType: any,
    value: string,
    userId?: string,
    addUserIdToQuery = false,
  ): Promise<firebase.firestore.QuerySnapshot> {
    let query: firebase.firestore.Query;
    if (addUserIdToQuery) {
      query = collection.where('userId', '==', userId).where(param, queryType, value);
    } else {
      query = collection.where(param, queryType, value);
    }
    return query.get().then(snapshot => snapshot as firebase.firestore.QuerySnapshot)
    .catch((err) => {
      this.logError(err.message);
      console.log('Error retrieving ', collection.path);
      console.log('where', param, queryType, value);
      addUserIdToQuery && console.log('where', 'userId', '==', userId);
      console.log('Error retrieving the documents from firebase. ', err);
      throw (err);
    });
  }

  async getGeneralConstants() {
    this.getDocumentObs('businessConstants', 'generalConstants', this.generalConstantsSource);
    this.getDocumentObs('businessConstants', 'portfolioConstants', this.portfolioConstantsSource);
    this.getDocumentObs('businessConstants', 'holidays', this.holidaysSource);
    this.getDocumentObs('businessConstants', 'holidaysUS', this.holidaysUSSource);
    this.getDocumentObs('businessConstants', 'portfolioLabels', this.portfolioLabelsSource);
  }

  getBusinessConstantsGeneralConstants() {
    return this.firestore.collection('businessConstants').doc('generalConstants').get().then((doc) => {
      return doc.data();
    });
  }

  getBusinessConstantsPortfolioConstants() {
    return this.firestore.collection('businessConstants').doc('portfolioConstants').get().then((doc) => {
      return doc.data();
    });
  }

  uploadFileFromStorage(file: Blob | File, fileName: string, firebasePath: string): firebase.storage.UploadTask {
    const storageRef = firebase.storage().ref(`${firebasePath}/\/${fileName}`);
    return storageRef.put(file);
  }

  deleteDocument(collection: firebase.firestore.CollectionReference, id: string): Promise<any> {
    return collection.doc(id).delete();
  }

  // downloadFileFromStorage(url: string) {
  //   const defaultRequestOptions = new HttpRequest({ responseType: 3 }, );
  //   return this.http.get(url, defaultRequestOptions)
  //     .map(res => res.json())
  //     .catch((e) => {
  //       console.log('Error downloading the document from firebase.');
  //       throw e;
  //     });
  // }

  getFileURLFromStorage(fileName: string, firebasePath: string) {
    const storageRef = firebase.storage().ref(`${firebasePath}/\/${fileName}`);
    return storageRef.getDownloadURL().then((url) => {
      return url;
    })
    .catch((e) => {
      console.log('Error downloading the document from firebase.');
      throw e;
    });
  }

  getDocumentObs(collection: string, id: string, subject: BehaviorSubject<any>, obj?: any) {
    return this.firestore.collection(collection).doc(id)
    .onSnapshot(doc => {
      subject.next({...doc.data(), ...obj});
    },
    (err) => { subject.next(null); });
  }

  getSnapshotObs(
    collection: string,
    key: string,
    value: string | boolean,
    subject: BehaviorSubject<any>,
    obj?: any,
    orderByKey?: string,
    orderByDirection?: string,
  ) {
    let query = this.firestore.collection(collection).where(key, '==', value);
    if (orderByKey) {
      query = query.orderBy(orderByKey, orderByDirection === 'desc' ? 'desc' : 'asc');
    }
    return query
    .onSnapshot(snap => {
      subject.next([...snap.docs.map(d => {
        return {
          ...d.data(),
          id: d.id,
        };
      })]);
    });
  }

  getSnapshotWithUidObs(collection: string, key: string, value: string | boolean, subject: BehaviorSubject<any>, uid: string, obj?: any) {
    return this.firestore.collection(collection).where('userId', '==', uid).where(key, '==', value)
    .onSnapshot(snap => {
      subject.next([...snap.docs.map(d => {
        return {
          ...d.data(),
          id: d.id,
        };
      })]);
    });
  }

  async isEmptyQuery(query: firebase.firestore.Query) {
    const result = await query.limit(1).get();
    return result.empty;
  }

  observeQuery(
    query: firebase.firestore.Query,
    subject: BehaviorSubject<any>,
    parseResultsFn: (snap: firebase.firestore.QuerySnapshot) => any,
  ) {
    query.onSnapshot((querySnapshot) => {
      subject.next(parseResultsFn(querySnapshot));
    });
  }

  onQuerySnapshot(
    query: firebase.firestore.Query,
    callback: (snap: firebase.firestore.QuerySnapshot) => any,
  ) {
    query.onSnapshot(callback);
  }

  logEvent(type: string, data: {}, toIntercom = false) {
    if (this.platform.is('cordova')) {
      this.firebaseNative.logEvent(type, data);
      if (environment.production && !environment.isPartnerSite) {
        this.smartlook.trackCustomEvent(new SmartlookCustomEvent(type, data));
      }
      if (toIntercom) {
        this.intercom.logEvent(type, data);
      }
    } else {
      firebase.analytics().logEvent(type, data);
    }
    if (this.fb && this.fb.logEvent) {
      const fbParams = { data: data ? data.toString() : 'no_data' };
      this.fb.logEvent(type || 'no_event_name', fbParams);
    }
  }

  setUserId(uid: string) {
    if (this.platform.is('cordova')) {
      this.firebaseNative.setUserId(uid);
      this.firebaseNative.setCrashlyticsUserId(uid);
    } else {
      firebase.analytics().setUserId(uid);
    }
  }

  setCrashlyticsUserId(uid: string) {
    if (this.platform.is('cordova')) {
      this.firebaseNative.setCrashlyticsUserId(uid);
    }
  }

  setScreen(name: string) {
    if (this.platform.is('cordova')) {
      this.firebaseNative.setScreenName(name);
      this.logEvent('page_' + name.toLowerCase().trim().replace(/ /g, '_'), {});
    } else {
      firebase.analytics().setCurrentScreen(name);
      firebase.analytics().logEvent('page_' + name.toLowerCase().trim().replace(/ /g, '_'), {});
    }
  }

  setUserProperty(name: string, value: string) {
    if (this.platform.is('cordova')) {
      this.firebaseNative.setUserProperty(name, value);
    }
  }

  logError(error) {
    if (this.platform.is('cordova')) {
      this.firebaseNative.logError(error);
    }
  }

  subscribe(topic: string) {
    if (this.platform.is('cordova')) {
      this.firebaseNative.subscribe(topic);
    }
  }

  isWorkingDay(date: Date, checkCL: boolean, checkUS?: boolean) {
    const chileDate = date.toLocaleString('es-CL', {timeZone: 'America/Santiago'});
    const chileDayOfTheWeek = date.toLocaleString('es-CL', {timeZone: 'America/Santiago', weekday: 'long'});
    // If sunday or saturday in chile
    if (chileDayOfTheWeek === 'domingo' || chileDayOfTheWeek === 'sábado') {
      return false;
    } else if (this.holidaysSource.getValue() && this.holidaysUSSource.getValue()) {
      const holidays = this.holidaysSource.getValue().values;
      const holidaysUS = this.holidaysUSSource.getValue().values;
      const stringDate = chileDate.replace(',', '').split(' ')[0].split('-').reverse().join('');
      if (checkCL && holidays.find((d) => d === stringDate)) {
        return false;
      }
      if (checkUS && holidaysUS.find((d) => d === stringDate)) {
        return false;
      }
      return true;
    }
  }

  isInDrivewealthBuySellSchedule(margin?: number) {
    if (!this.isWorkingDay(new Date(), false, true)) { return { marketOpen: false, extendedHours: false }; }
    const now = moment.tz('America/New_york');
    const marketCloseTime = moment.tz('America/New_york').set({
      hours: 16,
      minutes: 0 + (margin ? margin : 0),
      seconds: 0,
      milliseconds: 0,
    });
    const marketOpenTime = moment.tz('America/New_york').set({
      hours: 9,
      minutes: 30 - (margin ? margin : 0),
      seconds: 0,
      milliseconds: 0,
    });
    const preMarketOpenTime = moment.tz('America/New_york').set({
      hours: 4,
      minutes: 0 + (margin ? margin + 2 : 0),
      seconds: 40,
      milliseconds: 0,
    });
    const postMarketCloseTime = moment.tz('America/New_york').set({
      hours: 20,
      minutes: 0 - (margin ? margin + 2 : 0),
      seconds: 0,
      milliseconds: 0,
    });
    const marketOpen = now.isAfter(marketOpenTime) && now.isBefore(marketCloseTime);
    const extendedHours = !marketOpen && now.isAfter(preMarketOpenTime) && now.isBefore(postMarketCloseTime);
    return {marketOpen, extendedHours};
  }

  getNextWorkingDay(date: Date, checkUS?: boolean, usOnly?: boolean) {
    let nextDay = Utils.addDays(date, 1);
    while (!this.isWorkingDay(nextDay, !usOnly, checkUS)) {
      nextDay = Utils.addDays(nextDay, 1);
    }
    return nextDay;
  }

  getPreviousWorkingDate(date: Date, checkCL: boolean, checkUS: boolean, pastDays = 1) {
    let previousDay = date;
    previousDay.setHours(22);
    previousDay.setMinutes(0);
    for (let i = 0; i < pastDays; i++) {
      previousDay = Utils.addDays(previousDay, -1);
      while (!this.isWorkingDay(previousDay, checkCL, checkUS)) {
        previousDay = Utils.addDays(previousDay, -1);
      }
    }
    return previousDay;
  }

  async callableFunction(name: string): Promise<string> {
    try {
      const callable = this.functions.httpsCallable(name);
      return callable().then((result) => {
        return result.data;
      });
    } catch (e) {
      console.log(e);
      return 'ERR';
    }
  }

  async getStringConfigValue(key: string): Promise<string> {
    if (!this.platform.is('cordova')) {
      return defaultConfig[key];
    }
    const value = await this.firebaseNative.getValue(key);
    return value || (value === undefined && defaultConfig[key]) || '';
  }

  async getBooleanConfigValue(key: string): Promise<boolean> {
    if (!this.platform.is('cordova')) {
      return defaultConfig[key] === 'true';
    }
    const value = await this.firebaseNative.getValue(key);
    return value === true || value === 'true' || (value === undefined && defaultConfig[key] === 'true');
  }

  setRemoteConfig() {
    if (!this.platform.is('cordova')) {
      return;
    }
    this.platform.is('android') && this.firebaseNative.setDefaults(defaultConfig);
    this.firebaseNative.fetch().then(() => {
      this.firebaseNative.activateFetched();
    });
  }

  async listFoldersInDirectory(directoryPath: string): Promise<string[]> {
    const storageRef = this.storage.ref(directoryPath);
    return storageRef.listAll().then(result => {
      const folderNames = result.prefixes.map(folderRef => folderRef.name);
      return folderNames;
    });
  }

  getGeneralConstantsCurrentValue() {
    return this.generalConstantsSource.getValue();
  }
}
